import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import useViewport from 'Hooks/useViewport';
import useSliderSettings from 'Hooks/SiteSlider/useSliderSettings';
import useCalculateSlideWidth from 'Hooks/SiteSlider/useCalculateSlideWidth';
import pauseYTVideos from 'Helpers/Video/VideoHelpers';

/**
 * @param {Array.object} children - Array of children to use as slides.
 * @param {Object} sliderSettings - Settings to customise the slider. If none are provided, defualts below will be used.
 * @param {string} sliderClass - Class that can be added to the top level of the slider.
 * @param {boolean} sliderLightTheme - Boolean to force the light theme colours.
 * @param {boolean} sliderCentered - Boolean to center slides when there are fewer that slidesToShow. Default is left aligned.
 * @param {boolean} preloadSlides - Boolean to control whether the slider has been interacted with. This is used to force all slides to load.
 * @param {boolean} fullWidth - Boolean to control whether a slide displays at 100% width when there is 1 slideToShow. By default this will be 90% so that the next slide is partially visible.
 * @param {boolean} resetCarousel - Boolean to control whether the slider resets to slide 1 (sliderPosition 0) if the children change. This is true by default but can be overwritten.
 * @param {boolean} containsYouTubeVideos - Boolean to tell the slider there are YouTube videos in the slide. These can then be paused when the slide changes.
 * @param {function} afterClickFunction - Function that will be called when clicking an arrow or dot. Only works on LG and above. Returns the current active slide number.
*/

const SiteSlider = React.memo(({
  children,
  sliderSettings,
  sliderClass,
  sliderLightTheme,
  sliderCentered,
  preloadSlides,
  fullWidth,
  resetCarousel,
  containsYouTubeVideos,
  afterClickFunction,
}) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [sliderInteracted, setSliderInteracted] = useState(preloadSlides || false);

  const sliderRef = useRef(null);
  const slidesRef = useRef([]);

  // Strings/numbers survive Children.toArray but break the cloneElement below
  const allSlides = React.Children.toArray(children).filter(React.isValidElement);

  const { isBelowLg, isAboveXs, isAboveMd } = useViewport();

  // Get the slider settings using the custom hook
  // This will return the settings based on the current viewport and any custom settings provided
  // If no custom settings are provided, it will use the default settings defined in the hook
  const { settings } = useSliderSettings(sliderSettings, allSlides.length);
  const {
    slidesToShow,
    slidesToScroll,
    sliderWidth,
    sliderGap,
    showDots,
    showArrows,
    slideVisiblePercent,
  } = settings;

  // Calculate the width of each slide using the custom hook
  // This will return the width based on the current viewport, number of slides to show, and other relevant settings
  const slideWidth = useCalculateSlideWidth(
    allSlides,
    fullWidth,
    sliderGap,
    sliderWidth,
    slidesToShow,
    slideVisiblePercent,
  );

  // Reset the carousel only when the set of slides actually changes.
  // We key on the slide keys (e.g. product IDs)
  const slidesSignature = allSlides.map(child => child.key).join('|');
  useEffect(() => {
    if (resetCarousel) {
      setSliderPosition(0);
      setActiveSlide(0);
    }
  }, [slidesSignature, resetCarousel]);

  // Intersection Observer to detect which slide is most in view on mobile
  useEffect(() => {
    if (!sliderRef.current || isAboveMd || !showDots) return undefined; // Only for mobile with dots

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Check if more than 50% of the slide is visible
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            // Find which slide this element corresponds to
            const slideIndex = slidesRef.current.findIndex(slide => slide === entry.target);
            if (slideIndex !== -1 && slideIndex !== activeSlide) {
              setActiveSlide(slideIndex);
              if (afterClickFunction) afterClickFunction(slideIndex);
            }
          }
        });
      },
      {
        root: sliderRef.current, // Use the slider container as the root
        threshold: [0.1, 0.5, 0.9], // Multiple thresholds for better detection
        rootMargin: '0px',
      },
    );

    // Observe all slides that are loaded
    slidesRef.current.forEach((slide, index) => {
      if (slide && (index < slidesToShow || sliderInteracted)) {
        observer.observe(slide);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [
    isAboveMd,
    activeSlide,
    showDots,
    sliderInteracted,
    slidesToShow,
    afterClickFunction,
  ]);

  // Function to handle clicking on a dot
  const clickDot = (dotNumber) => {
    if (containsYouTubeVideos) {
      pauseYTVideos();
    }
    // On desktop, clicking a dot changes the sliderposition
    if (isAboveMd) {
      setSliderPosition(-((slideWidth * dotNumber) + (sliderGap * dotNumber)));
    }
    // On mobile, clicking a dot scrolls the slider to the correct position
    if (!isAboveMd) {
      sliderRef.current.scrollLeft = (slideWidth * dotNumber) + (sliderGap * dotNumber);
    }
    setActiveSlide(dotNumber);
    setSliderInteracted(true);
    if (afterClickFunction) afterClickFunction(dotNumber);
  };

  // Function to handle clicking the left or right arrow
  const clickArrow = (direction) => {
    // How many slides are not in a full set. This is used to calculate how many slides to scroll.
    const slidesRemainder = allSlides.length % slidesToShow;

    setSliderInteracted(true);

    if (containsYouTubeVideos) {
      pauseYTVideos();
    }

    if (direction === 'left') {
      if (slidesToScroll > 1) {
        // If were on a remainder slide, go back by the remainder otherwise go back slidesToShow
        if (activeSlide % slidesToScroll !== 0) {
          setSliderPosition(sliderPosition + ((slideWidth * slidesRemainder) + (sliderGap * slidesRemainder))); // eslint-disable-line max-len
          setActiveSlide(activeSlide - slidesRemainder);
          if (afterClickFunction) afterClickFunction(activeSlide - slidesRemainder);
        } else {
          setSliderPosition(sliderPosition + ((slideWidth * slidesToScroll) + (sliderGap * slidesToScroll))); // eslint-disable-line max-len
          setActiveSlide(activeSlide - slidesToScroll);
          if (afterClickFunction) afterClickFunction(activeSlide - slidesToScroll);
        }
      } else {
        setSliderPosition(sliderPosition + (slideWidth + sliderGap));
        setActiveSlide(activeSlide - 1);
        if (afterClickFunction) afterClickFunction(activeSlide - 1);
      }
    }

    if (direction === 'right') {
      if (slidesToScroll > 1) {
        // If clicking right would set activeSlide to more than the length of the children (allSlides) array, set the active slide to the last slide
        if ((activeSlide + slidesToScroll) >= allSlides.length - slidesRemainder) {
          setSliderPosition(sliderPosition - ((slideWidth * slidesRemainder) + (sliderGap * slidesRemainder))); // eslint-disable-line max-len
          setActiveSlide(activeSlide + slidesRemainder);
          if (afterClickFunction) afterClickFunction(activeSlide + slidesRemainder);
        } else {
          setSliderPosition(sliderPosition - ((slideWidth * slidesToScroll) + (sliderGap * slidesToScroll))); // eslint-disable-line max-len
          setActiveSlide(activeSlide + slidesToScroll);
          if (afterClickFunction) afterClickFunction(activeSlide + slidesToScroll);
        }
      } else {
        setSliderPosition(sliderPosition - (slideWidth + sliderGap));
        setActiveSlide(activeSlide + 1);
        if (afterClickFunction) afterClickFunction(activeSlide + 1);
      }
    }
  };

  // If use scrolls on a touch device, set interacted to true so that the slides render
  const handleScroll = () => {
    if (isBelowLg && !sliderInteracted) {
      setSliderInteracted(true);
    }
  };

  // This is added to the SiteSlider__slidesContainer to add space around the slides.
  // This is needed on desktop to ensure that the label and shadwow are not cut off.
  // On mobile this is not needed as the slides meet the edge of the screen.
  let sliderContainerMargin = 0;
  if (isAboveXs) sliderContainerMargin = sliderGap / 2;

  return (
    <>
      <section
        className={classNames(`SiteSlider ${sliderClass}`, { 'SiteSlider--single': allSlides.length === 1 }, { 'SiteSlider--dots': showDots }, { 'SiteSlider--lightTheme': sliderLightTheme })}
        style={{
          width: isAboveXs ? `${sliderWidth}px` : 'auto',
        }}
      >
        <div
          className="SiteSlider__slidesContainer"
          style={{
            marginLeft: `-${sliderContainerMargin}px`,
            marginRight: `-${sliderContainerMargin}px`,
            paddingLeft: `${sliderContainerMargin}px`,
            paddingRight: `${sliderContainerMargin}px`,
          }}
        >
          <div
            className={classNames('SiteSlider__slides', { 'SiteSlider__slides--centered': sliderCentered && allSlides.length <= slidesToShow })}
            ref={sliderRef}
            style={{ left: `${sliderPosition}px`, gap: `${sliderGap}px` }}
            onScroll={handleScroll}
          >
            {allSlides.map((child, index) => {
              // Only load elements in the DOM which are in view, or after the slider has been interacted with
              let loadSlide = (index < slidesToShow || sliderInteracted);
              if (isBelowLg && (index <= slidesToShow || sliderInteracted)) {
                loadSlide = true;
              }
              if (isAboveMd && slidesToShow === 1 && (index <= slidesToShow || sliderInteracted)) {
                loadSlide = true;
              }

              // Work out if slide is hidden outside of the view
              let isSlideHidden = index >= (activeSlide + slidesToShow) || index < activeSlide;
              if (isBelowLg || slidesToShow === 1) {
                isSlideHidden = index >= (activeSlide + slidesToShow + 1) || index < activeSlide;
              }

              return (
                <div
                  className={classNames('SiteSlider__slide', { 'SiteSlider__slide--hidden': isSlideHidden && !sliderInteracted })}
                  style={{ width: slideWidth }}
                  ref={(element) => { slidesRef.current[index] = element; }}
                  key={`slide_${child.key}`}
                  aria-hidden={isSlideHidden}
                  tabIndex={-1}
                >
                  {loadSlide && React.cloneElement(child)}
                </div>
              );
            })}
          </div>
        </div>

        {showArrows &&
          <>
            <div className="SiteSlider__arrow SiteSlider__arrow--left">
              <button aria-label="Previous" disabled={activeSlide === 0} onClick={() => { clickArrow('left'); }}>
                <FontAwesomeIcon icon={faChevronLeft} title="Previous" />
              </button>
            </div>

            <div className="SiteSlider__arrow SiteSlider__arrow--right">
              <button aria-label="Next" disabled={activeSlide === allSlides.length - slidesToShow} onClick={() => { clickArrow('right'); }}>
                <FontAwesomeIcon icon={faChevronRight} title="Next" />
              </button>
            </div>
          </>
        }

        {showDots &&
          <div className="SiteSlider__dots">
            {allSlides.slice(0, allSlides.length - (slidesToShow - 1)).map((dot, index) => {
              // Show 1 dot per child, minus the last slide as this dot will not move the panel.
              // If we scroll more than 1 slide at a time. Hide the dots that are skipped.
              let hideDot = false;
              if (
                slidesToScroll > 1 &&
                (index % slidesToScroll !== 0) &&
                index + 1 !== allSlides.length - (slidesToShow - 1)
              ) {
                hideDot = true;
              }
              return (
                <button
                  className={classNames('SiteSlider__dots__dot', { 'SiteSlider__dots__dot--active': activeSlide === index }, { 'SiteSlider__dots__dot--hidden': hideDot })}
                  aria-label={`Go to slide ${index + 1} of ${allSlides.length - 1}`}
                  key={`dot_${dot.key}`}
                  tabIndex={0}
                  onClick={() => { clickDot(index); }}
                >
                  <div />
                </button>
              );
            },
            )}
          </div>
        }
      </section>
    </>
  );
});

SiteSlider.propTypes = {
  sliderSettings: PropTypes.shape({}),
  sliderClass: PropTypes.string,
  sliderLightTheme: PropTypes.bool,
  sliderCentered: PropTypes.bool,
  preloadSlides: PropTypes.bool,
  fullWidth: PropTypes.bool,
  resetCarousel: PropTypes.bool,
  containsYouTubeVideos: PropTypes.bool,
  afterClickFunction: PropTypes.func,
};

SiteSlider.defaultProps = {
  sliderSettings: {},
  sliderClass: '',
  sliderLightTheme: false,
  sliderCentered: false,
  preloadSlides: false,
  fullWidth: false,
  resetCarousel: true,
  containsYouTubeVideos: false,
  afterClickFunction: () => {},
};

export default SiteSlider;
